// src/api/taskReports.ts

import api from './api';
import { 
  OverviewStats, 
  EmployeesResponse, 
  WorkloadResponse, 
  TasksReportResponse, 
  TimelineResponse,
  EmployeeDetail,
  ReportFilters 
} from '../types/taskReport';

// Description: Get org-wide task overview
// Endpoint: GET /tasks/reports/overview
// Request: { start_date?: string, end_date?: string, department_id?: string }
// Response: { date_range, total_tasks, by_status, by_priority, done_count, overdue_count, completion_rate_pct, overdue_rate_pct, avg_completion_days, avg_open_progress_pct, tasks_created_in_period, tasks_completed_in_period }
export const getTaskOverview = async (filters: ReportFilters = {}) => {
  try {
    console.log('API: Fetching task overview with filters:', filters);
    const response = await api.get('/tasks/reports/overview', {
      params: filters
    });
    console.log('API: Successfully fetched task overview');
    return response.data;
  } catch (error: any) {
    console.error('API: Error fetching task overview:', error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};

// Description: Get employee performance table
// Endpoint: GET /tasks/reports/employees
// Request: { start_date?: string, end_date?: string, department_id?: string }
// Response: { total: number, employees: EmployeePerformance[] }
export const getEmployeePerformance = async (filters: ReportFilters = {}) => {
  try {
    console.log('API: Fetching employee performance with filters:', filters);
    const response = await api.get('/tasks/reports/employees', {
      params: filters
    });
    console.log('API: Successfully fetched employee performance');
    return response.data;
  } catch (error: any) {
    console.error('API: Error fetching employee performance:', error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};

// Description: Get deep report for one employee
// Endpoint: GET /tasks/reports/employee/{user_id}
// Request: { user_id: string, start_date?: string, end_date?: string }
// Response: { user, date_range, summary, by_priority, tasks, recent_activity }
export const getEmployeeDetailReport = async (userId: string, filters: ReportFilters = {}) => {
  try {
    console.log(`API: Fetching employee detail report for ${userId} with filters:`, filters);
    const response = await api.get(`/tasks/reports/employee/${userId}`, {
      params: filters
    });
    console.log('API: Successfully fetched employee detail report');
    return response.data;
  } catch (error: any) {
    console.error(`API: Error fetching employee detail report for ${userId}:`, error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};

// Description: Get full task list with assignees, checklist, comments
// Endpoint: GET /tasks/reports/tasks
// Request: { start_date?: string, end_date?: string, department_id?: string, status?: string, priority?: string, assigned_to?: string, include_closed?: boolean, page?: number, page_size?: number }
// Response: { total: number, page: number, page_size: number, tasks: TaskReport[] }
export const getTaskListReport = async (params: {
  start_date?: string;
  end_date?: string;
  department_id?: string;
  status?: string;
  priority?: string;
  assigned_to?: string;
  include_closed?: boolean;
  page?: number;
  page_size?: number;
}) => {
  try {
    console.log('API: Fetching task list report with params:', params);
    const response = await api.get('/tasks/reports/tasks', {
      params: params
    });
    console.log('API: Successfully fetched task list report');
    return response.data;
  } catch (error: any) {
    console.error('API: Error fetching task list report:', error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};

// Description: Get live open-task workload per employee
// Endpoint: GET /tasks/reports/workload
// Request: { department_id?: string }
// Response: { total: number, workload: WorkloadItem[] }
export const getWorkloadReport = async (filters: { department_id?: string } = {}) => {
  try {
    console.log('API: Fetching workload report with filters:', filters);
    const response = await api.get('/tasks/reports/workload', {
      params: filters
    });
    console.log('API: Successfully fetched workload report');
    return response.data;
  } catch (error: any) {
    console.error('API: Error fetching workload report:', error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};

// Description: Get tasks grouped by client
// Endpoint: GET /tasks/reports/clients
export const getClientReport = async (filters: { start_date?: string; end_date?: string; client_id?: string } = {}) => {
  const response = await api.get('/tasks/reports/clients', { params: filters });
  return response.data;
};

// Description: Get tasks for a specific client
// Endpoint: GET /tasks/reports/clients/{client_id}/tasks
export const getClientTasks = async (clientId: string, filters: { start_date?: string; end_date?: string; status?: string } = {}) => {
  const response = await api.get(`/tasks/reports/clients/${clientId}/tasks`, { params: filters });
  return response.data;
};

// Description: Get tasks grouped by project
// Endpoint: GET /tasks/reports/projects
export const getProjectReport = async (filters: { start_date?: string; end_date?: string; project_id?: string } = {}) => {
  const response = await api.get('/tasks/reports/projects', { params: filters });
  return response.data;
};

// Description: Get tasks for a specific project
// Endpoint: GET /tasks/reports/projects/{project_id}/tasks
export const getProjectTasks = async (projectId: string, filters: { start_date?: string; end_date?: string; status?: string } = {}) => {
  const response = await api.get(`/tasks/reports/projects/${projectId}/tasks`, { params: filters });
  return response.data;
};

// Description: Get timeline chart data for tasks created/completed over time
// Endpoint: GET /tasks/reports/timeline
// Request: { start_date?: string, end_date?: string, group_by?: 'month' | 'week', department_id?: string }
// Response: { group_by: string, date_range, timeline: TimelinePoint[] }
export const getTimelineReport = async (params: {
  start_date?: string;
  end_date?: string;
  group_by?: 'month' | 'week';
  department_id?: string;
}) => {
  try {
    console.log('API: Fetching timeline report with params:', params);
    const response = await api.get('/tasks/reports/timeline', {
      params: params
    });
    console.log('API: Successfully fetched timeline report');
    return response.data;
  } catch (error: any) {
    console.error('API: Error fetching timeline report:', error);
    throw new Error(error?.response?.data?.detail || error?.response?.data?.error || error.message);
  }
};